// Style definitions the ORIGINAL's content needs in the redline.
//
// The output package is the revised document's, so its styles.xml is the revised draft's. Text
// carried in from the original (a deleted paragraph or table, a deleted comment reference) can
// name a style the revised draft does not have: a deleted table then loses its table style, a
// deleted heading its heading formatting, a deleted comment mark its "annotation reference"
// formatting (and --classic-styles could not give such a run its style back). Like the footnote
// merge, copy in from the original exactly the styles that are referenced and missing, with the
// styles they are based on or linked to. A style the revised draft also has is never touched:
// its formatting wins, as in Word's Compare.
//
// (Lists are not merged: a deleted list item whose list the revised draft no longer has shows
// without its number. Copying the original's list in would number the deleted items as a list
// of their own, which can show a wrong number; see the stress report of 2026-10-24.)

const STYLE_REF = /<w:(?:pStyle|rStyle|tblStyle|basedOn|link)\b[^>]*?\bw:val="([^"]*)"/g;

/** styleId → whole `<w:style …>…</w:style>` element. */
function stylesById(xml: string): Map<string, string> {
  const out = new Map<string, string>();
  for (const m of xml.matchAll(/<w:style\b[^>]*?(?:\/>|[^/]>[\s\S]*?<\/w:style>)/g)) {
    const id = (m[0].match(/^<w:style\b[^>]*?\bw:styleId="([^"]*)"/) || [])[1];
    if (id != null && !out.has(id)) out.set(id, m[0]);
  }
  return out;
}

/** Declare on the part's root every namespace prefix `copied` uses that the root lacks, taking
 *  the declaration from `fromRoot` (the original part's root start tag). */
function declarePrefixes(xml: string, copied: string, fromRoot: string): string {
  const root = (xml.match(/<w:styles\b[^>]*>/) || [''])[0];
  if (!root) return xml;
  const declared = new Set([...root.matchAll(/\sxmlns:([\w.-]+)="/g)].map(m => m[1]));
  const used = new Set([...copied.matchAll(/[<\s]([A-Za-z][\w.-]*):[\w.-]+/g)].map(m => m[1]));
  let add = '';
  for (const p of used) {
    if (p === 'xmlns' || p === 'xml' || declared.has(p)) continue;
    const d = fromRoot.match(new RegExp(`\\sxmlns:${p.replace(/\./g, '\\.')}="[^"]*"`));
    if (d) add += d[0];
  }
  return add ? xml.replace(root, root.replace(/\s*>$/, `${add}>`)) : xml;
}

/** Copy into the output's styles.xml the styles its story parts use that only the original
 *  defines. Returns how many were copied. */
export async function mergeOriginalOnlyStyles(newZip: any, origZip: any): Promise<number> {
  if (!origZip) return 0;
  const outXml: string | undefined = await newZip.file('word/styles.xml')?.async('string');
  const origXml: string | undefined = await origZip.file('word/styles.xml')?.async('string');
  if (outXml == null || !origXml) return 0;
  let text = '';
  for (const n of ['word/document.xml', 'word/footnotes.xml', 'word/endnotes.xml', 'word/comments.xml']) {
    const f = newZip.file(n);
    if (f) text += await f.async('string');
  }
  const out = stylesById(outXml), orig = stylesById(origXml);
  // the closure: styles the content names, the styles those are based on or linked to, …
  const add: string[] = [];
  const seen = new Set<string>();
  const queue = [text];
  while (queue.length) {
    for (const m of queue.pop()!.matchAll(STYLE_REF)) {
      const id = m[1];
      if (seen.has(id)) continue;
      seen.add(id);
      if (out.has(id) || !orig.has(id)) continue;
      add.push(id);
      queue.push(orig.get(id)!);
    }
  }
  if (!add.length) return 0;
  const copied = add.map(id => orig.get(id)!).join('');
  const merged = outXml.replace(/<\/w:styles>\s*$/, copied + '</w:styles>');
  if (merged === outXml) return 0;
  newZip.file('word/styles.xml', declarePrefixes(merged, copied, (origXml.match(/<w:styles\b[^>]*>/) || [''])[0]));
  return add.length;
}
